<template>
  <label v-if="label" :for="inputId" :class="['inp-label', {'a11y-blind': hideLabel}, labelClass]">
    {{ label }}
    <span v-if="astRequired" class="text-red">*</span>
  </label>
  <div class="tarea-box-wrap" :class="{'focus': isFocused}">
    <div class="textarea-box" :class="[{'rounded-top8 no-border-btm' : bottomContent}, textAreaBoxClass]">
      <textarea 
        v-model="textValue" 
        ref="textareaRef" 
        :placeholder="placeholder" 
        :name="name" 
        :id="inputId" 
        :rows="rows" 
        :maxlength="maxChars" 
        @blur="handleTextareaBlur" 
        @focus="isFocused = true" 
        @input="handleTextAreaInput" 
        :required="required" 
        :disabled="(disabled || isReadonly)"
        >
      </textarea>
      
      <div v-if="showHtmlPlaceholder" class="fake-Placeholder" v-html="htmlPlaceholder"></div>

      <p class="counter">
        <span class="text-green">{{ textValue.length }}</span>/{{ maxChars }}
      </p>
    </div>
    <!-- 툭수문자 영역 -->
    <div v-if="bottomContent" class="bottom-box" :class="charsBoxClass">
      <div class="btn-box">
        <div class="lft-box">
          <button type="button" :class="['btn btn-outline up-arrow', {'down': showSpecialChars}]" @click="toggleSpecialChars">특수문자</button>
        </div>
        <div v-if="showRgtButton" class="rgt-box">
          <button type="button" @click="$emit('messageLoad', textValue)" class="btn btn-outline">최근/MY메시지 불러오기</button>
          <button type="button" @click="$emit('save', textValue)" class="btn ml8">저장하기</button>
        </div>
      </div>
      <div v-if="showSpecialChars" class="special-char-box">
        <ul class="special-char-list" :class="charListClass">
          <li v-for="char in specialChars" :key="char">
            <button type="button" @click="addChar(char)">{{ char }}</button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
const inputId = useId();

interface Props {
  modelValue: string | null
  placeholder?: string
  bottomContent?: boolean
  rows?: number | string
  maxRows?: number | string
  maxChars?: number | string
  showRgtButton?: boolean
  label?: string
  hideLabel?: boolean
  labelClass?: string
  astRequired?: boolean
  name?: string
  showHtmlPlaceholder?: boolean
  htmlPlaceholder?: string
  charsBoxClass?: string
  textAreaBoxClass?: string
  charListClass?: string
  required?: boolean
  disabled?: boolean
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '내용을 입력해주세요',
  bottomContent: false,
  rows: 5,
  maxRows: 10,
  maxChars: 200,
  label: '',
  hideLabel: false,
  labelClass: '',
  astRequired: false,
  name: '',
  showHtmlPlaceholder: false,
  htmlPlaceholder: '',
  charsBoxClass: '',
  textAreaBoxClass: '',
  charListClass: '',
  required: false,
  disabled: false,
  readonly: false
})

const emit = defineEmits(['update:modelValue', 'messageLoad', 'save', 'blur'])

const textValue = ref<string>(props.modelValue ?? '')
const showSpecialChars = ref<boolean>(false)
const specialChars: string[] = ['`', '~', '!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '=', '+', '[', ']', '{', '}', ';', "'", ':', '＼', ',', '.', '/', '<', '>', '?', '♥', '♡', '★', '☆', '●', '○', '■', '□', '◀', '◁', '▶', '▷', '◆', '◇', '※', '☎', '♬'];

const handleTextAreaInput = ():void => {
  emit('update:modelValue', textValue.value)
}

const isReadonly = computed(() => props.readonly)

// placeholder 문장일 경우 입력값이 있거나 포커스 상태일 때는 placeholder 숨김
const isFocused = ref(false)
const showHtmlPlaceholder = computed(() => {
  return !props.modelValue && !isFocused.value
})

const handleTextareaBlur = (event: FocusEvent) => {
  isFocused.value = false
  emit('blur', event)
}


// 특수문자
const addChar = (char: string): void => {
  const currentLen = textValue.value.length
  const maxLen = Number(props.maxChars)
  if (currentLen < maxLen) {
    textValue.value += char
    emit('update:modelValue', textValue.value)
  }
}

const toggleSpecialChars = (): void => {
  showSpecialChars.value = !showSpecialChars.value
}

watch(() => props.modelValue, (newVal) => {
  textValue.value = newVal ?? ''
})

// textarea FOCUS
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const focus = () => {
  textareaRef.value?.focus()
}
defineExpose({ focus })

</script>

<style lang="scss" scoped>
</style>